"use client";

import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const rows = [{
  number: "01",
  eyebrow: "LAVAGEM DE TANQUES & BARRICAS",
  headline: "Lavagem de Cubas & Barricas",
  body: "Cabeças rotativas de limpeza com cobertura 360° e baixo consumo de água",
  cta: "Ver produtos",
  ctaHref: "/TankCleaningPage",
  numberColor: "hsl(215 80% 20%)",
  imageLeft: true,
  image: {
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.48.03.png?v=8mctqusci3c",
    alt: "Equipamento de lavagem e sanitização de cubas e barricas de vinho"
  }
}, {
  number: "02",
  eyebrow: "CONTROLO DE HUMIDADE",
  headline: "Humidificação de Adegas",
  body: "Sistemas de nebulização para controlo de humidade durante o estágio em madeira, reduzindo perdas por evaporação",
  cta: "Ver aplicações",
  ctaHref: "/ApplicationsPage",
  numberColor: "hsl(215 90% 45%)",
  imageLeft: false,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/humidificacao_adegas_vinho.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/humidificacao_adegas_vinho.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/humidificacao_adegas_vinho.png-wvc-sizes",
    alt: "Sistema de humidificação por nebulização em adega de barricas de vinho"
  }
}, {
  number: "03",
  eyebrow: "HIGIENIZAÇÃO & ENCHIMENTO",
  headline: "Higienização de Linhas de Enchimento",
  body: "Pulverização e desinfeção automatizada que asseguram a estabilidade microbiológica do vinho",
  cta: "Ver soluções",
  ctaHref: "/DisinfectionSystemsPage",
  numberColor: "hsl(158 68% 42%)",
  imageLeft: true,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/higienizacao_linha_enchimento_vinho.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/higienizacao_linha_enchimento_vinho.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/higienizacao_linha_enchimento_vinho.png-wvc-sizes",
    alt: "Sistema automatizado de pulverização para higienização em linha de enchimento de vinho"
  }
}];
export default function PremiumContentShowcase() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".engine-header-eyebrow", {
        y: 16
      });
      gsap.set(".engine-header-title", {
        y: 24
      });
      gsap.to(".engine-header-eyebrow", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 82%",
          once: true
        }
      });
      gsap.to(".engine-header-title", {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        delay: 0.1,
        scrollTrigger: {
          trigger: el,
          start: "top 82%",
          once: true
        }
      });
      el.querySelectorAll<HTMLElement>(".engine-row").forEach(row => {
        const image = row.querySelector<HTMLElement>(".engine-image");
        const textEls = row.querySelectorAll<HTMLElement>(".engine-text-el");
        if (image) {
          gsap.set(image, {
            filter: "blur(10px)"
          });
          gsap.to(image, {
            opacity: 1,
            filter: "blur(0px)",
            duration: 0.9,
            ease: "power3.out",
            scrollTrigger: {
              trigger: row,
              start: "top 72%",
              once: true
            }
          });
        }
        gsap.set(textEls, {
          y: 24
        });
        gsap.to(textEls, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          stagger: 0.09,
          ease: "power3.out",
          scrollTrigger: {
            trigger: row,
            start: "top 72%",
            once: true
          }
        });
      });
      const refresh = () => ScrollTrigger.refresh();
      requestAnimationFrame(refresh);
      if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
        once: true
      });
      document.fonts?.ready.then(refresh);
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".engine-header-eyebrow", ".engine-header-title", ".engine-image", ".engine-text-el"], {
        opacity: 1,
        y: 0,
        filter: "blur(0px)"
      });
    });
  }, {
    scope: container
  });
  return;
}
function EngineArrowLink({
  to,
  label
}: {
  to: string;
  label: string;
}) {
  return <Link to={to} className="group relative inline-flex items-center gap-3 font-default text-base font-normal" style={{
    color: "var(--foreground)"
  }}>
      <span className="relative">
        {label}
        <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 transition-transform duration-[240ms] ease-out group-hover:scale-x-100" style={{
        backgroundColor: "var(--foreground)"
      }} />
      </span>
      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-[240ms] ease-out group-hover:translate-x-1" strokeWidth={1.25} />
    </Link>;
}